import React, { useEffect, useState } from 'react';
import { formatINR } from '@/utils/format';
import { getOrderCostBreakdown, type OrderCostBreakdown } from '@/services/costingService';

interface StandardCostDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  materials?: any[];
  workOrders?: any[];
}

export const StandardCostDetailsModal: React.FC<StandardCostDetailsModalProps> = ({
  open,
  onClose,
  order,
}) => {
  const [breakdown, setBreakdown] = useState<OrderCostBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const orderRef = String(order?.id || order?.po_number || '');

  // Live standard cost breakdown of the order (BOM, work orders, operator rates, Costing Setup)
  useEffect(() => {
    if (!open || !orderRef) return;
    let cancelled = false;
    setLoading(true);
    getOrderCostBreakdown(orderRef)
      .then((res) => {
        if (!cancelled) setBreakdown(res?.success && res.data ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setBreakdown(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderRef]);

  if (!open) return null;

  const plannedQty = Number(breakdown?.plannedQty ?? order.planned_qty ?? 0);
  const perUnit = (amount: number) => (plannedQty > 0 ? Math.round(amount / plannedQty) : 0);
  const poNumber = breakdown?.poNumber || order.po_number || '';
  const productName = breakdown?.productName || order.item_name || '';
  const productVariant = breakdown?.productCode || order.item_code || '';
  const status = breakdown?.status || order.status || '';
  const unit = breakdown?.unit || order.unit || '';

  const formatQty = (n: number) => (n % 1 === 0 ? String(n) : n.toFixed(2));
  const allRows = (breakdown?.elements || [])
    .filter((e) => e.stdAmount !== 0 || e.stdQty !== 0)
    .map((e) => ({
      element: e.description,
      type: e.type as string,
      qty: e.type === 'OVERHEAD' ? `${e.rate}%` : `${formatQty(e.stdQty)} ${e.unit}`,
      rate: e.type === 'OVERHEAD' ? e.stdAmount : e.rate,
      stdCost: e.stdAmount,
      costPerUnit: perUnit(e.stdAmount),
    }));

  const materialTotal = breakdown?.standard.material ?? 0;
  const labourTotal = breakdown?.standard.labour ?? 0;
  const overheadTotal = breakdown?.standard.overhead ?? 0;
  const totalStandardCost = breakdown?.standard.total ?? 0;
  const perUnitCost = perUnit(totalStandardCost);

  const pctOfTotal = (amount: number) => (totalStandardCost > 0 ? ((amount / totalStandardCost) * 100).toFixed(1) : '0.0');
  const matPct = pctOfTotal(materialTotal);
  const labPct = pctOfTotal(labourTotal);
  const ovhPct = pctOfTotal(overheadTotal);

  // Profitability: item sale price × planned quantity
  const salePrice = Number(breakdown?.salePrice ?? 0);
  const totalRevenue = salePrice * plannedQty;
  const grossMargin = totalRevenue - totalStandardCost;
  const marginPct = totalRevenue > 0 ? ((grossMargin / totalRevenue) * 100).toFixed(1) : '0.0';

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* ── Modal Header ── */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">
            Standard Cost Details — {poNumber}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[82vh] overflow-y-auto">
          {/* ── 1. ORDER INFO ── */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              ORDER INFO
            </h3>
            <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-4 grid grid-cols-2 gap-y-4 gap-x-6">
              <div>
                <span className="text-xs text-slate-400 block mb-0.5">PO Number</span>
                <span className="text-sm font-bold text-slate-900">{poNumber}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block mb-0.5">Product</span>
                <span className="text-sm font-bold text-slate-900 block">{productName}</span>
                <span className="text-xs text-slate-400 block mt-0.5">{productVariant}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block mb-0.5">Quantity</span>
                <span className="text-sm font-bold text-slate-900">{plannedQty} {unit}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 block mb-1">Status</span>
                <span className={`inline-block px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                  status === 'DRAFT'
                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                    : status === 'PLANNED' || status === 'CONFIRMED'
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : status === 'COMPLETED'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    : 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                }`}>
                  {status}
                </span>
              </div>
            </div>
          </div>

          {/* ── 2. COST ELEMENT BREAKDOWN ── */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              COST ELEMENT BREAKDOWN
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-xs text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 font-semibold select-none">
                    <th className="py-2.5 px-3 text-left">Element</th>
                    <th className="py-2.5 px-2 text-center">Type</th>
                    <th className="py-2.5 px-3 text-right">Qty</th>
                    <th className="py-2.5 px-3 text-right">Rate</th>
                    <th className="py-2.5 px-3 text-right font-bold text-slate-700">Std Cost</th>
                    <th className="py-2.5 px-3 text-right">Cost / Unit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 font-medium">
                  {allRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-4 px-3 text-center text-slate-400">
                        {loading ? 'Loading cost breakdown…' : 'No standard cost elements found for this order'}
                      </td>
                    </tr>
                  )}
                  {allRows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-2.5 px-3 text-slate-800 font-medium whitespace-nowrap">
                        {row.element}
                      </td>
                      <td className="py-2.5 px-2 text-center whitespace-nowrap">
                        {row.type === 'MATERIAL' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-blue-50 text-blue-600 border border-blue-200/80 inline-block">
                            MATERIAL
                          </span>
                        )}
                        {row.type === 'LABOUR' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-emerald-50 text-emerald-600 border border-emerald-200/80 inline-block">
                            LABOUR
                          </span>
                        )}
                        {row.type === 'OVERHEAD' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-purple-50 text-purple-600 border border-purple-200/80 inline-block">
                            OVERHEAD
                          </span>
                        )}
                        {row.type === 'REJECTION' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider rounded-full uppercase bg-rose-50 text-rose-600 border border-rose-200/80 inline-block">
                            REJECTION
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-600 whitespace-nowrap">
                        {row.qty}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-600 whitespace-nowrap">
                        {formatINR(row.rate)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-bold text-slate-900 whitespace-nowrap">
                        {formatINR(row.stdCost)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-slate-600 whitespace-nowrap">
                        {formatINR(row.costPerUnit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── 3. COST SUMMARY ── */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              COST SUMMARY
            </h3>
            <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-4 space-y-2.5 text-xs">
              <div className="flex justify-between items-center text-slate-600">
                <span>Material Total</span>
                <span className="font-semibold text-slate-900">
                  {formatINR(materialTotal)} <span className="text-slate-400 font-normal">({matPct}% of total)</span>
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Labour Total</span>
                <span className="font-semibold text-slate-900">
                  {formatINR(labourTotal)} <span className="text-slate-400 font-normal">({labPct}%)</span>
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Overhead Total</span>
                <span className="font-semibold text-slate-900">
                  {formatINR(overheadTotal)} <span className="text-slate-400 font-normal">({ovhPct}%)</span>
                </span>
              </div>
              <div className="border-t border-slate-200 pt-2.5 flex justify-between items-center text-sm font-bold text-slate-900">
                <span>TOTAL STANDARD COST</span>
                <span>{formatINR(totalStandardCost)}</span>
              </div>
              <div className="flex justify-between items-center text-xs font-bold text-slate-800">
                <span className="font-medium text-slate-500">Per Unit</span>
                <span>{formatINR(perUnitCost)}</span>
              </div>
            </div>
          </div>

          {/* ── 4. PROFITABILITY ── */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              PROFITABILITY
            </h3>
            <div className="bg-slate-50/70 border border-slate-100 rounded-xl p-4 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-600">
                <span>Sale Price</span>
                <span className="font-semibold text-slate-900">{formatINR(salePrice)}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Total Revenue ({plannedQty} units)</span>
                <span className="font-semibold text-slate-900">{formatINR(totalRevenue)}</span>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Total Standard Cost</span>
                <span className="font-semibold text-slate-900">{formatINR(totalStandardCost)}</span>
              </div>
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="font-bold text-slate-900">Gross Margin</span>
                <div className="text-right">
                  <span className="font-bold text-slate-900 text-sm">
                    {formatINR(grossMargin)} <span className="text-slate-500 font-medium text-xs">({marginPct}%)</span>
                  </span>
                  <div className="text-[11px] font-bold text-emerald-600 mt-0.5">
                    Grade A Excellent
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── 5. PRINT ACTION BUTTON ── */}
          <button
            onClick={handlePrint}
            className="w-full h-11 rounded-xl bg-[#0f172a] hover:bg-slate-800 text-white font-semibold text-xs tracking-wider transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer"
          >
            <i className="ri-printer-line text-sm" /> Print Cost Sheet
          </button>
        </div>
      </div>
    </div>
  );
};
